/**
 * The case page's walk through the queue: "Next case", "Previous", "Mark done and next",
 * and the time on the case for the effort measurement.
 *
 * The order comes from the snapshot the queue wrote when the case was opened
 * (`next-case.ts`). When it runs out, the next queue page is read with the same filters.
 * Without a snapshot (a shared link, a new tab) "Next case" is not offered.
 *
 * The activity events (`case_view`, `case_leave`, `next_case`) feed the effort
 * measurement, which is read only per area and week. They never show in the history.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

import {
  hasMoreRows,
  neighbours,
  nextPageSearch,
  type QueueOrder,
  readQueueOrder,
  saveQueueOrder,
  withClosed,
  withMore,
} from "@/features/case-review/next-case";
import {
  QUEUE_PAGE_SIZE,
  queueFiltersFromParams,
  queueHref,
} from "@/features/queue/queue-defaults";
import { fetchQueuePage, sendCaseActivity } from "@/lib/api";
import { FEATURES } from "@/lib/features";
import { logger } from "@/lib/logger";
import { track } from "@/lib/usage";

export interface CaseFlow {
  /** True when the case was opened from the queue (the order is known). */
  fromQueue: boolean;
  previous: string | null;
  next: string | null;
  /** "12" of the queue; null without an order. */
  position: number | null;
  total: number | null;
  /** True while the next queue page is read. */
  loadingNext: boolean;
  goNext: () => void;
  goPrevious: () => void;
  /** After closing this case: skip it from now on and open the next one. */
  closedAndNext: () => void;
}

export function useCaseFlow(caseId: string): CaseFlow {
  const navigate = useNavigate();
  const [order, setOrder] = useState<QueueOrder | null>(() =>
    FEATURES.caseFlow ? readQueueOrder() : null,
  );
  const [loadingNext, setLoadingNext] = useState(false);
  const openedAt = useRef(Date.now());

  // One "case_view" per opened case, and "case_leave" with the time when it is left.
  useEffect(() => {
    openedAt.current = Date.now();
    void sendCaseActivity(caseId, { kind: "case_view" });
    const leave = () =>
      void sendCaseActivity(
        caseId,
        { kind: "case_leave", ms_on_case: Math.max(0, Date.now() - openedAt.current) },
        true,
      );
    window.addEventListener("pagehide", leave);
    return () => {
      window.removeEventListener("pagehide", leave);
      leave();
    };
  }, [caseId]);

  const { previous, next, position } = neighbours(order, caseId);

  const open = useCallback(
    (target: string, kind: "next" | "previous") => {
      if (kind === "next") {
        void sendCaseActivity(caseId, {
          kind: "next_case",
          ms_on_case: Math.max(0, Date.now() - openedAt.current),
        });
      }
      logger.debug("[case] flow", { from: caseId, to: target, kind });
      track("case.next", { dir: kind }, { caseId });
      navigate(`/case/${encodeURIComponent(target)}`);
    },
    [caseId, navigate],
  );

  const readMore = useCallback(
    async (base: QueueOrder): Promise<string | null> => {
      if (!hasMoreRows(base)) {
        return null;
      }
      setLoadingNext(true);
      try {
        const search = nextPageSearch(base, QUEUE_PAGE_SIZE);
        const page = await fetchQueuePage(
          queueFiltersFromParams(new URLSearchParams(search), new Date()),
        );
        const grown = withMore(
          base,
          page.items.map((row) => row.id),
          page.total,
        );
        saveQueueOrder(grown);
        setOrder(grown);
        return neighbours(grown, caseId).next;
      } catch (error) {
        logger.warn("[case] next queue page could not be read", error);
        return null;
      } finally {
        setLoadingNext(false);
      }
    },
    [caseId],
  );

  const forward = useCallback(
    (base: QueueOrder | null) => {
      const target = neighbours(base, caseId).next;
      if (target) {
        open(target, "next");
        return;
      }
      if (!base) {
        navigate(queueHref());
        return;
      }
      void readMore(base).then((found) => {
        if (found) {
          open(found, "next");
        } else {
          navigate(queueHref());
        }
      });
    },
    [caseId, navigate, open, readMore],
  );

  const goNext = useCallback(() => forward(order), [forward, order]);
  const goPrevious = useCallback(() => {
    if (previous) {
      open(previous, "previous");
    }
  }, [open, previous]);
  const closedAndNext = useCallback(() => {
    if (!order) {
      return;
    }
    const updated = withClosed(order, caseId);
    saveQueueOrder(updated);
    setOrder(updated);
    forward(updated);
  }, [order, caseId, forward]);

  return {
    fromQueue: order != null && position != null,
    previous,
    next,
    position,
    total: order ? order.total : null,
    loadingNext,
    goNext,
    goPrevious,
    closedAndNext,
  };
}
